import { Link } from "@tanstack/react-router";
import type { MapRow, MapStep } from "@/app/analyticsQueries";
import { initials } from "@/app/nav";
import { CELL_LOOK, LEGEND, cellTitle, mentorNames, mentorTone, trackGroups } from "./model";

/** Карта прохождения: строка — студент, столбец — шаг маршрута (29). Клетка —
 *  состояние гейта, не процент просмотра. Подсказка «имя: шаг · состояние». */
export function Matrix({ steps, rows }: { steps: MapStep[]; rows: MapRow[] }) {
  const groups = trackGroups(steps);
  const order = mentorNames(rows);
  const cells = { gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` };
  return (
    <section className="overflow-hidden rounded-md border border-bd bg-card" data-testid="matrix">
      <div className="flex flex-wrap items-baseline gap-3 border-b border-bd px-4 py-3">
        <h2 className="m-0 font-mono text-[14px] font-bold">Карта прохождения</h2>
        <span className="font-mono text-[11.5px] text-mut">строка — студент, столбец — шаг маршрута</span>
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[760px]">
          <div className="grid grid-cols-[168px_78px_minmax(0,1fr)] items-center border-b border-bd px-2 py-[9px] font-mono text-[10px] uppercase tracking-[.04em] text-mut">
            <span className="pl-1">студент</span>
            <span>ментор</span>
            <div className="grid gap-[2px]" style={cells} data-testid="track-heads">
              {groups.map((g, i) => (
                <span
                  key={g.track}
                  className={`truncate px-1 normal-case tracking-normal ${i ? "border-l border-bd" : ""}`}
                  style={{ gridColumn: `span ${g.n}` }}
                  title={`${g.track} · ${g.n} шагов`}
                >
                  {g.track}
                </span>
              ))}
            </div>
          </div>
          {rows.map((r) => (
            <div key={r.id} className="grid grid-cols-[168px_78px_minmax(0,1fr)] items-center border-b border-bd px-2 py-[7px] last:border-b-0 hover:bg-card2" data-testid="map-row" data-done={r.done} data-stuck={r.stuck || undefined}>
              <span className="flex min-w-0 items-center gap-2">
                <span aria-hidden className="grid size-[22px] flex-none place-items-center rounded-full border border-bd2 bg-card2 font-mono text-[9.5px] font-semibold text-ink">
                  {initials(r.name)}
                </span>
                <Link to="/mentor/student/$userId" params={{ userId: r.id }} className={`truncate text-[12.5px] no-underline hover:underline ${r.stuck ? "text-ink" : "text-ink2"}`}>
                  {r.name}
                </Link>
              </span>
              <span className={`truncate font-mono text-[11.5px] ${mentorTone(r.mentor, order)}`} data-testid="row-mentor">
                {r.mentor || "—"}
              </span>
              <div className="grid gap-[2px]" style={cells} role="list" aria-label={`${r.name}: сдано ${r.done} из ${steps.length}`}>
                {steps.map((s, i) => {
                  const state = r.cells[i] ?? "не начат";
                  return <span key={s.id} role="listitem" title={cellTitle(r, s, state)} data-testid="cell" data-state={state} className={`block h-[18px] rounded-[2px] border ${CELL_LOOK[state]}`} />;
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      <Legend />
    </section>
  );
}

/** Легенда — образец клетки + подпись, не только цвет. */
export function Legend() {
  return (
    <ul className="m-0 flex flex-wrap gap-x-5 gap-y-2 border-t border-bd px-4 py-2.5 pl-4 font-mono text-[11px] text-ink2" data-testid="legend">
      {LEGEND.map((l) => (
        <li key={l.state} className="flex items-center gap-2">
          <span aria-hidden className={`inline-block h-[12px] w-[18px] rounded-[2px] border ${CELL_LOOK[l.state]}`} />
          {l.label}
        </li>
      ))}
    </ul>
  );
}
